import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';
import { api } from '../api';
import { STATIC } from '../staticMode';
import { vocabStore } from '../staticStore';
import { entryKey as keyOf } from '../utils/wordText';
import { queueVocabSync, onVocabChange } from '../utils/vocabSync';

// Vocab is keyed per dialect — "dialect:word" (lowercased word) — so status/SRS state are
// independent per dialect: a word known in Truku does NOT show known in Tgdaya.
// The builder is shared with useGloss now; see entryKey.

// Backend-backed vocab. Loads the user's whole vocab map once into state so the
// read API stays synchronous (components call getStatus during render); writes
// update state optimistically and persist to the server.
export function useVocab() {
  const { token } = useAuth();
  const [vocab, setVocab] = useState(() => (STATIC ? vocabStore.getAll() : {}));
  const [ready, setReady] = useState(STATIC);   // static: localStorage is ready synchronously

  useEffect(() => {
    if (STATIC) return;                          // static loads from localStorage (above)
    setReady(false);
    if (!token) { setVocab({}); setReady(true); return; }
    let alive = true;
    api('/vocab', { token }).then(d => { if (alive) { setVocab(d || {}); setReady(true); } }).catch(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [token]);

  // A background cloud merge (another device, or the review hub) can rewrite
  // localStorage without this component ever calling persist — pick it up.
  useEffect(() => {
    if (!STATIC) return;
    return onVocabChange((cards) => setVocab(cards));
  }, []);

  const persist = useCallback((key, patch) => {
    if (STATIC) { vocabStore.setKey(key, patch); queueVocabSync(); return; }
    if (!token) return;
    api(`/vocab/${encodeURIComponent(key)}`, { method: 'PUT', token, body: patch }).catch(() => {});
  }, [token]);

  const getStatus = useCallback((dialect, wordText) => {
    if (!wordText) return 'new';
    return vocab[keyOf(dialect, wordText)]?.status || 'new';
  }, [vocab]);

  // `loc` (optional, stored on 'learning') = where the word was marked, {code, itemId, wi}
  // — itemId is whatever unit the popup was on (segment id in 片, sentence id in 句/文,
  // para_N in 段). 複習's 跳到故事 resolves it back to the exact segment instead of
  // jumping to the word's first occurrence. Merged server-side, so grading keeps it.
  const setStatus = useCallback((dialect, wordText, status, loc) => {
    if (!wordText) return;
    const key = keyOf(dialect, wordText);
    const patch = (status === 'learning' && loc) ? { status, loc } : { status };
    setVocab(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
    persist(key, patch);
  }, [persist]);

  const getCard = useCallback((dialect, wordText) => {
    return vocab[keyOf(dialect, wordText)] || {};
  }, [vocab]);

  const updateCard = useCallback((dialect, wordText, cardData) => {
    const key = keyOf(dialect, wordText);
    setVocab(prev => ({ ...prev, [key]: { ...prev[key], ...cardData } }));
    persist(key, cardData);
  }, [persist]);

  // Parse "dialect:word" back so the SRS review knows each card's dialect.
  const getLearningWords = useCallback(() => {
    return Object.entries(vocab)
      .filter(([, card]) => card.status === 'learning')
      .map(([key, card]) => {
        const i = key.indexOf(':');
        return { ...card, dialect: i < 0 ? null : key.slice(0, i), word: i < 0 ? key : key.slice(i + 1) };
      });
  }, [vocab]);

  return { getStatus, setStatus, getCard, updateCard, getLearningWords, vocab, ready };
}
